import api, { apiFullResponse } from 'services/api'
import { stringifyFiltersQuery, stringifyPageQuery, uploadFile } from 'services/utils'
import { client as websocketClient } from 'services/SocketClient'

export const uploadAttachment = (file, type) => {
  return uploadFile(file, '/order-attachments', [{ name: 'type', value: type }])
}

export const addAttachmentToOrder = (orderId, file, type) => {
  return uploadFile(file, `/orders/${orderId}/attachments`, [{ name: 'type', value: type }, { name: 'sendNotification', value: false } ])
}

export const removeAttachmentFromOrder = (orderId, attachmentId) => {
  return api.delete(`/orders/${orderId}/attachments/${attachmentId}`)
}

export const createOrder = (values) => api.post('/orders', values)

export const editOrder = ({ id, ...values }) => api.put(`/orders/${id}`, values)

export const fetchOrder = (id) => api.get(`/orders/${id}`)

export const sendForApproval = (id) => api.post(`/orders/${id}/actions/send-request-for-approval`)

export const fetchOrders = (params) => api.get(`/orders?${stringifyPageQuery(params)}`)

export const getStatusBar = (id) => api.get(`/orders/${id}/progress-bar`)

export const getOrderActions = (id) => api.get(`/orders/${id}/actions`)

export const getOrderFinancialSummary = (filters) => api.get(`/orders/financial-summary?${stringifyFiltersQuery(filters)}`)

export const getOrderAction = (id) => api.get(`/orders/actions/${id}`)

export const fetchOrderToken = (id) => api.get(`/contractor-view/${id}/order-token`)

export const fetchOrderByToken = (token) => api.get(`/contractor-view?orderToken=${token}`)

export const editStatusChange = (id, values) => api.put(`/orders/actions/status-changes/${id}`, values)

export const approveCargo = (id, values) =>
  api.put(`/orders/actions/${id}/approve-cargo`, values)

export const rejectCargo = (id) =>
  api.put(`/orders/actions/${id}/reject-cargo`)

export const getOrderAllowedStatusChanges = (id) => api.get(`/orders/${id}/actions/allowed-status-changes`)

export const getOrderMailingHistory = (id) => api.get(`/orders/${id}/email-send-attempts`)

export const setOrderStatus = (id, newStatus, values) => {
  const { dateTime, timeZone, sendEmail, cargo, cancellationCause, rejectionCause } = values
  switch (newStatus.status) {
    case OrderStatus.Saved:
      return api.post(`/orders/${id}/actions/save`, { dateTime, timeZone })
    case OrderStatus.AcceptedForFulfillment:
      return api.post(`/orders/${id}/actions/accept-for-fulfillment`, { sendEmail, dateTime, timeZone })
    case OrderStatus.SentToDriver:
      return api.post(`/orders/${id}/actions/send`, { dateTime, timeZone })
    case OrderStatus.ReadByDriver:
      return api.post(`/orders/${id}/actions/read`, { dateTime, timeZone })
    case OrderStatus.AcceptedByDriver:
      return api.post(`/orders/${id}/actions/accept`, {
        sendEmail, dateTime, timeZone
      })
    case OrderStatus.Rejected:
      return api.post(`/orders/${id}/actions/reject`, { rejectionCause, dateTime, timeZone })
    case OrderStatus.Loading:
      return api.post(`/orders/${id}/actions/loading`, {
        position: newStatus.deliveryPointNumber,
        sendEmail,
        dateTime,
        timeZone
      })
    case OrderStatus.Loaded:
      return api.post(`/orders/${id}/actions/loaded`, {
        position: newStatus.deliveryPointNumber,
        sendEmail,
        dateTime,
        cargo,
        timeZone
      })
    case OrderStatus.Unloading:
      return api.post(`/orders/${id}/actions/unloading`, {
        position: newStatus.deliveryPointNumber,
        sendEmail,
        dateTime,
        timeZone
      })
    case OrderStatus.Unloaded:
      return api.post(`/orders/${id}/actions/unloaded`, {
        position: newStatus.deliveryPointNumber,
        sendEmail,
        dateTime,
        cargo,
        timeZone
      })
    case OrderStatus.OnFerryPassage:
      return api.post(`/orders/${id}/actions/on-ferry-passage`, {
        intermediatePointPosition: newStatus.intermediatePointPosition,
        dateTime,
        sendEmail,
        timeZone
      })
    case OrderStatus.FerryPassageCompleted:
      return api.post(`/orders/${id}/actions/ferry-passage-completed`, {
        intermediatePointPosition: newStatus.intermediatePointPosition,
        dateTime,
        sendEmail,
        timeZone
      })
    case OrderStatus.OnCustomsControl:
      return api.post(`/orders/${id}/actions/on-customs-control`, {
        intermediatePointPosition: newStatus.intermediatePointPosition,
        dateTime,
        sendEmail,
        timeZone
      })
    case OrderStatus.CustomsControlCompleted:
      return api.post(`/orders/${id}/actions/customs-control-completed`, {
        intermediatePointPosition: newStatus.intermediatePointPosition,
        dateTime,
        sendEmail,
        timeZone
      })
    case OrderStatus.OnCustomIntermediatePoint:
      return api.post(`/orders/${id}/actions/on-custom-intermediate-point`, {
        intermediatePointPosition: newStatus.intermediatePointPosition,
        dateTime,
        sendEmail,
        timeZone
      })
    case OrderStatus.CustomIntermediatePointCompleted:
      return api.post(`/orders/${id}/actions/custom-intermediate-point-completed`, {
        intermediatePointPosition: newStatus.intermediatePointPosition,
        dateTime,
        sendEmail,
        timeZone
      })
    case OrderStatus.Finished:
      return api.post(`/orders/${id}/actions/finish`, { dateTime, timeZone })
    case OrderStatus.Cancelled:
      return api.post(`/orders/${id}/actions/cancel`, {
        cancellationCause, sendEmail, dateTime, timeZone
      })
    default:
      return Promise.reject('Unknown status')
  }
}

export const fetchFinishedOrdersCount = () => api.get('/finished-orders-count')

export const fetchDeliveryPointAddresses = (companyName) => api.get(`/delivery-point-addresses?companyName=${companyName}`)

export const updateComment = (id, values) => api.put(`/orders/${id}/comment`, values)

export const setAttachmentVerifiedAs = (attachmentId, verified) =>
  verified ? api.put(`/order-attachments/${attachmentId}/verified`) : api.put(`/order-attachments/${attachmentId}/not-verified`)

export const setAttachmentContractorAccessAs = (attachmentId, access) => access
  ? api.put(`/order-attachments/${attachmentId}/contractor-access`)
  : api.put(`/order-attachments/${attachmentId}/contractor-access-denied`)

export const setAttachmentDriverAccessAs = (attachmentId, access) => access ? api.put(`/order-attachments/${attachmentId}/driver-access`) : api.put(
  `/order-attachments/${attachmentId}/driver-access-denied`)

export const rejectAttachment = (attachmentId) =>
  api.put(`/order-attachments/${attachmentId}/reject`)

export function subscribeOrderUpdated (callback) {
  const sub = websocketClient.subscribe(`/topic/orderDataUpdated`, () => {
    callback()
  })
  return () => {
    sub.unsubscribe()
  }
}

export function subscribeOrderActionsUpdated (id, callback) {
  const sub = websocketClient.subscribe(`/topic/orderActionUpdate/${id}`, () => {
    callback()
  })
  return () => {
    sub.unsubscribe()
  }
}

export function subscribeEmailSent (id, callback) {
  const sub = websocketClient.subscribe(`/topic/emailSent/${id}`, () => {
    callback()
  })
  return () => {
    sub.unsubscribe()
  }
}

export const deleteOrder = id => api.delete(`/orders/${id}`)

export const fetchOrderHistory = (id) => api.get(`orders/${id}/history`)

export const getIsMerchantLimitExceeded = (id) => api.get(`orders/${id}/is-merchant-limit-exceeded`)

export const fetchTransactionServices = () => api.get(`/external/transaction-services`)

// note: it won't work with filenames containing semicolons or not quoted filenames
// if you want to use it somewhere else, proceed with caution
const parseAttachmentFilename = contentDisposition => {
  return contentDisposition.split('filename="')[1].split(';')[0].replace('"',
    '')
}

export const generateOrderForwardingSummaryPdf = (
  orderId, { sendEmail = false, language },
) =>
  apiFullResponse.post(`/orders/${orderId}/forwarding-summary-pdf`,
    { sendEmail, language }, { responseType: 'blob' }).then(response => {
    const filename = parseAttachmentFilename(
      response.headers['content-disposition'])
    const data = response.data
    return { filename, data }
  })

export const rateOrderByClient = (token, clientRatings) =>  api.post(`contractor-view/rate-order?orderToken=${token}`, clientRatings)

export const fetchIsOrderRatedByClient = (token) =>  api.get(`contractor-view/order-rating?orderToken=${token}`)

export const rateOrderByForwarder = (id, forwarderRatings) =>  api.post(`orders/${id}/rate`, forwarderRatings)

export const fetchOrderRating = (id) =>  api.get(`orders/${id}/rating`)

export const OrderStatus = {
  Saved: 'SAVED',
  SentToDriver: 'SENT_TO_DRIVER',
  ReadByDriver: 'READ_BY_DRIVER',
  AcceptedByDriver: 'ACCEPTED_BY_DRIVER',
  Rejected: 'REJECTED',
  AcceptedForFulfillment: 'ACCEPTED_FOR_FULFILLMENT',
  Loading: 'LOADING',
  Loaded: 'LOADED',
  Unloading: 'UNLOADING',
  Unloaded: 'UNLOADED',
  OnFerryPassage: 'ON_FERRY_PASSAGE',
  FerryPassageCompleted: 'FERRY_PASSAGE_COMPLETED',
  OnCustomsControl: 'ON_CUSTOMS_CONTROL',
  CustomsControlCompleted: 'CUSTOMS_CONTROL_COMPLETED',
  OnCustomIntermediatePoint: 'ON_CUSTOM_INTERMEDIATE_POINT',
  CustomIntermediatePointCompleted: 'CUSTOM_INTERMEDIATE_POINT_COMPLETED',
  Finished: 'FINISHED',
  Cancelled: 'CANCELLED'
}

export const OrderActionType = {
  OrderStatusChange: 'ORDER_STATUS_CHANGE'
}

export const OrderEmailNotificationsFrequencySettingsType = {
  Never: 'NEVER',
  MorningAndEvening: 'MORNING_AND_EVENING',
  Interval: 'INTERVAL'
}

export const OrderEmailsLanguage = {
  Polish: 'PL',
  English: 'EN',
  German: 'DE'
}
